import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Form, message } from 'antd'
import dayjs from 'dayjs'
import { DatePickerF, SelectF } from '@/i2antdV5'
import { parseHmStringToDayMinutes } from '@/utils/renderUtils'
import { fetchTuinaTherapistOnDuty } from '@/redux/tuina/therapistOnDutySlice'
import { fetchTuinaReservationSlots } from '@/redux/tuina/reservationSlotsSlice'
import './ReservationRescheduleForm.less'

const FormItem = Form.Item

function formatSlotMinutes (raw) {
  const n = Number(raw)
  if (!Number.isFinite(n)) return ''
  const hh = Math.floor(n / 60)
  const mm = n % 60
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`
}

function popupInSecondModal (trigger) {
  if (!trigger || typeof trigger.closest !== 'function') return document.body
  return trigger.closest('.tuina-rsv-reschedule-wrap') || document.body
}

class ReservationRescheduleForm extends React.Component {
  static propTypes = {
    form: PropTypes.object,
    record: PropTypes.object,
    therapistOnDuty: PropTypes.object,
    reservationSlots: PropTypes.object,
    dispatch: PropTypes.func
  }

  componentDidMount () {
    this.fetchTherapists()
    this.syncRecord()
  }

  componentDidUpdate (prevProps) {
    const prevId = prevProps.record && prevProps.record.id
    const nextId = this.props.record && this.props.record.id
    if (prevId === nextId) return
    this.fetchTherapists()
    this.syncRecord()
  }

  fetchTherapists = () => {
    const { dispatch, record } = this.props
    const query = {}
    if (record && record.storeId != null && record.storeId !== '') query.storeId = record.storeId
    dispatch(fetchTuinaTherapistOnDuty({ query }))
      .unwrap()
      .catch((err) => message.error(typeof err === 'string' ? err : '加载师傅失败'))
  }

  syncRecord = () => {
    const { form, record } = this.props
    if (!form || !form.current || !record) return
    form.current.setFieldsValue({
      therapistId: record.therapistId,
      appointmentDate: record.appointmentDate,
      startTime: undefined
    })
    this.fetchSlots({ keepStart: true })
  }

  fetchSlots = ({ keepStart } = {}) => {
    const { form, dispatch, record } = this.props
    if (!form || !form.current) return
    if (!keepStart) form.current.setFieldsValue({ startTime: undefined })
    const projectId = record && record.projectId
    const { therapistId, appointmentDate } = form.current.getFieldsValue()
    if (!projectId || !therapistId || !appointmentDate) return
    dispatch(fetchTuinaReservationSlots({
      query: { projectId, therapistId, date: appointmentDate }
    }))
      .unwrap()
      .then(() => {
        if (!keepStart) return
        if (!form.current || !record) return
        const start = parseHmStringToDayMinutes(record.startTime)
        if (start == null) return
        const values = form.current.getFieldsValue()
        if (String(values.therapistId) !== String(record.therapistId)) return
        if (String(values.appointmentDate) !== String(record.appointmentDate)) return
        form.current.setFieldsValue({ startTime: start })
      })
      .catch((err) => message.error(typeof err === 'string' ? err : '加载可约时间失败'))
  }

  handleValuesChange = (changed) => {
    if (!('therapistId' in changed) && !('appointmentDate' in changed)) return
    this.fetchSlots()
  }

  render () {
    const { form, record, therapistOnDuty, reservationSlots } = this.props
    const row = record || {}
    const therapistRows = Array.isArray(therapistOnDuty.data) ? therapistOnDuty.data : []
    const therapistOptions = therapistRows.map((item) => ({
      id: item.id,
      value: item.status === 'off_duty' ? `${item.name} 离` : item.name
    }))
    if (
      row.therapistId != null &&
      row.therapistId !== '' &&
      !therapistOptions.some((item) => String(item.id) === String(row.therapistId))
    ) {
      therapistOptions.unshift({
        id: row.therapistId,
        value: row.therapistName || '当前师傅'
      })
    }
    const therapistId = form && form.current ? form.current.getFieldValue('therapistId') : row.therapistId
    const appointmentDate = form && form.current ? form.current.getFieldValue('appointmentDate') : row.appointmentDate
    const projectId = row.projectId
    const ready = !!(projectId && therapistId && appointmentDate)
    const slotsQuery = reservationSlots.query
    const slotsMatch =
      slotsQuery.projectId === projectId &&
      slotsQuery.therapistId === therapistId &&
      slotsQuery.date === appointmentDate
    const slotRows = slotsMatch && Array.isArray(reservationSlots.data)
      ? reservationSlots.data.filter((item) => {
        if (item.startMinute == null || item.startMinute === '') return false
        return true
      })
      : []
    const currentStart = parseHmStringToDayMinutes(row.startTime)
    const slotOptions = slotRows.map((item) => {
      const unavailable = item.available !== true && Number(item.startMinute) !== Number(currentStart)
      const timeText = `${formatSlotMinutes(item.startMinute)} 至 ${formatSlotMinutes(item.endMinute)}`
      return {
        id: item.startMinute,
        value: unavailable ? `${timeText} 不可约` : timeText,
        selectDisabled: unavailable
      }
    })
    let placeholder = '请先选择师傅和日期'
    if (ready && reservationSlots.fetching) placeholder = '正在查询可约时间'
    if (ready && !reservationSlots.fetching && slotOptions.length) placeholder = '请选择时间'
    if (ready && !reservationSlots.fetching && !slotOptions.length) placeholder = '暂无可约时间'

    return (
      <Form ref={form} layout="vertical" onValuesChange={this.handleValuesChange}>
        <FormItem
          label="师傅"
          name="therapistId"
          rules={[{ required: true, message: '请选择师傅' }]}
        >
          <SelectF
            options={therapistOptions}
            modalProps={{
              size: 'large',
              placeholder: '请选择师傅',
              loading: therapistOnDuty.fetching,
              style: { width: '100%' },
              getPopupContainer: popupInSecondModal
            }}
          />
        </FormItem>
        <FormItem
          label="预约日期"
          name="appointmentDate"
          rules={[{ required: true, message: '请选择预约日期' }]}
        >
          <DatePickerF
            modalProps={{
              size: 'large',
              allowClear: false,
              format: 'YYYY-MM-DD',
              style: { width: '100%' },
              getPopupContainer: popupInSecondModal,
              disabledDate: (current) => current && current.isBefore(dayjs(), 'day')
            }}
          />
        </FormItem>
        <FormItem
          label="时间"
          name="startTime"
          extra="改师傅或日期后查询可约时间"
          rules={[{ required: true, message: '请选择时间' }]}
        >
          <SelectF
            options={slotOptions}
            modalProps={{
              size: 'large',
              disabled: !ready,
              loading: reservationSlots.fetching,
              placeholder,
              notFoundContent: '暂无可约时间',
              popupClassName: 'tuina-rsv-slot-dropdown',
              style: { width: '100%' },
              getPopupContainer: popupInSecondModal
            }}
          />
        </FormItem>
      </Form>
    )
  }
}

const mapStateToProps = (state) => ({
  therapistOnDuty: state.tuina.therapistOnDuty,
  reservationSlots: state.tuina.reservationSlots
})

export default connect(mapStateToProps, null, null, { forwardRef: true })(ReservationRescheduleForm)
